import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { adminAPI } from '../../utils/api';

const AssignmentCreate = ({ onAssignmentCreated }) => {
  const [candidates, setCandidates] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [candidatesData, assessmentsData] = await Promise.all([
        adminAPI.getCandidates(),
        adminAPI.getAssessments(),
      ]);

      setCandidates(candidatesData.candidates);
      setAssessments(assessmentsData.assessments);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setDataLoading(false);
    }
  };

  const handleAssessmentToggle = (assessmentId) => {
    setSelectedAssessments((prev) => {
      if (prev.includes(assessmentId)) {
        return prev.filter((id) => id !== assessmentId);
      } else {
        return [...prev, assessmentId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedAssessments.length === 0) {
      setError('Please select at least one assessment');
      return;
    }

    setLoading(true);

    try {
      const result = await adminAPI.createAssignments(
        selectedCandidate,
        selectedAssessments
      );

      setSuccess(`Successfully assigned ${result.assignments.length} assessment(s)!`);
      setSelectedCandidate('');
      setSelectedAssessments([]);

      if (onAssignmentCreated) {
        onAssignmentCreated(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to create assignments');
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Assign Assessments to Candidate
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {candidates.length === 0 || assessments.length === 0 ? (
        <Alert severity="info">
          {candidates.length === 0
            ? 'Please create candidates first.'
            : 'Please create assessments first.'}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Select Candidate</InputLabel>
            <Select
              value={selectedCandidate}
              onChange={(e) => setSelectedCandidate(e.target.value)}
              label="Select Candidate"
            >
              {candidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Assessments
            </Typography>
            <FormGroup>
              {assessments.map((assessment) => (
                <FormControlLabel
                  key={assessment.id}
                  control={
                    <Checkbox
                      checked={selectedAssessments.includes(assessment.id)}
                      onChange={() => handleAssessmentToggle(assessment.id)}
                    />
                  }
                  label={`${assessment.name} (${assessment.question_count} questions)`}
                />
              ))}
            </FormGroup>
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading || !selectedCandidate}
            sx={{ mt: 2 }}
          >
            {loading ? 'Assigning...' : 'Assign Assessments'}
          </Button>
        </form>
      )}
    </Paper>
  );
};

export default AssignmentCreate;
